import { privacyUrl, smsTermsUrl } from '@frontend/constants';
import { profileCompletionFormDefaultValues, useProfileCompletionForm } from '@frontend/forms';
import { signUpRouteValidator } from '@frontend/member-route-validators';
import { useEffect, useFeatureIsOn, useState } from '@frontend/react';
import { useShouldRedirectToFirstTask } from '@frontend/react-domain/paywall';
import {
  Anchor,
  AppShell,
  Banner,
  BirthdayInputMask,
  Button,
  Checkbox,
  PhoneNumberInput,
  Text,
  XStack,
  YStack,
} from '@frontend/web-react';
import { setOnboardingStep } from '@frontend/web-utils';
import {
  AppContentWrapper,
  CalendarCard,
  OnboardingCard,
  OnboardingLogoutButton,
  OnboardingPageWrapper,
  UnauthenticatedAppHeader,
} from '@frontend/web/components';
import { useEditProfileMutation, useListTasksQuery, useRetrieveClientQuery } from '@frontend/web/hooks';
import { createFileRoute, handleMutation, handleRedirectBeforeLoad } from '@frontend/web/utils';

type ProfileContentProps = {
  privacyPolicyUrl?: string;
  smsTermsUrl?: string;
};

const ProfileContent = ({
  privacyPolicyUrl = privacyUrl,
  smsTermsUrl: smsTermsHref = smsTermsUrl,
}: ProfileContentProps = {}) => {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const retrieveClientHookResult = useRetrieveClientQuery();
  const { data: client } = retrieveClientHookResult;
  const [editProfile, { isLoading }] = useEditProfileMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { enabled: isCalendarEnabled } = useFeatureIsOn('is_signup_calendar_enabled');
  const shouldRedirectToFirstTask = useShouldRedirectToFirstTask(client);
  const { refetch: refetchTasks } = useListTasksQuery(undefined);

  const form = useProfileCompletionForm({
    initialValues: profileCompletionFormDefaultValues,
    onSubmit: async (values) => {
      setSubmitError(null);
      await handleMutation({
        mutation: () =>
          editProfile({
            birthday: values.birthday,
            isSmsMarketingOptedIn: values.isSmsMarketingOptedIn,
            onboardingComplete: true,
            phoneNumber: values.phoneNumber,
          }).unwrap(),
        onError: () => {
          setSubmitError('Something went wrong. Please try again.');
        },
        onSuccess: async () => {
          if (searchParams.description) {
            navigate({
              search: { description: searchParams.description },
              to: '/app/draft-task/create',
            });
          } else if (shouldRedirectToFirstTask) {
            const result = await refetchTasks();
            const firstTask = result.data?.[0];
            if (firstTask?.id) {
              navigate({ params: { taskId: firstTask.id }, to: '/app/task/$taskId' });
            } else {
              navigate({ to: '/app/home' });
            }
          } else {
            navigate({ to: '/app/home' });
          }
        },
        showSuccessToast: false,
      });
    },
  });
  const { errors, getControl, setFieldValue, submitForm } = form;
  useEffect(() => {
    if (client?.phoneNumber || client?.birthday) {
      setFieldValue('phoneNumber', client.phoneNumber);
      setFieldValue('birthday', client.birthday);
    }
  }, [client, setFieldValue]);

  const isValid = !errors.birthday && !errors.phoneNumber;

  useEffect(() => {
    setOnboardingStep('web_onboarding_profile');
  }, []);

  return (
    <AppShell>
      <UnauthenticatedAppHeader linkTo={false} rightContent={<OnboardingLogoutButton step="profile" />} />
      <AppContentWrapper className="flex-1">
        <OnboardingPageWrapper currentStep="moreAboutYou">
          <OnboardingCard title="More about you">
            {submitError ? <Banner color="danger" leftIconName="IconAlertCircle" title={submitError} /> : null}

            <YStack gapY="md">
              <PhoneNumberInput
                disabled={isLoading || !!client?.phoneNumber}
                labelTextProps={{ className: 'font-medium', type: 'h6' }}
                {...getControl('phoneNumber')}
                readOnly={!!client?.phoneNumber}
              />
              <BirthdayInputMask
                error={getControl('birthday').error}
                labelTextProps={{ className: 'font-medium' }}
                onChange={(val) => getControl('birthday').onChange(val as DateISOString)}
                value={getControl('birthday').value}
              />
            </YStack>

            <YStack gapY="xs">
              <Checkbox
                checked={getControl('isSmsMarketingOptedIn').value}
                label="Opt in to Duckbill marketing texts"
                onChange={getControl('isSmsMarketingOptedIn').onChange}
              />
              <Text color="secondary" type="caption">
                Receive marketing and promotional SMS messages from Duckbill. Message frequency varies. Reply HELP for
                help or STOP to cancel. Message and data rates may apply.
              </Text>
              <XStack gapX="sm">
                <Anchor href={smsTermsHref} type="caption" withUnderline>
                  SMS Terms
                </Anchor>
                <Anchor href={privacyPolicyUrl} type="caption" withUnderline>
                  Privacy Policy
                </Anchor>
              </XStack>
            </YStack>
          </OnboardingCard>

          {isCalendarEnabled ? (
            <OnboardingCard title="Connect your calendar and Gmail">
              <CalendarCard />
            </OnboardingCard>
          ) : null}
          <Button
            disabled={!isValid || isLoading}
            isLoading={isLoading}
            label="Continue"
            onClick={submitForm}
            variant="primary"
          />
        </OnboardingPageWrapper>
      </AppContentWrapper>
    </AppShell>
  );
};

export const Route = createFileRoute('/onboarding/profile')({
  beforeLoad: async ({ context, location }) => {
    handleRedirectBeforeLoad({ context, location });
  },
  component: ProfileContent,
  validateSearch: signUpRouteValidator,
});
